import type { FailedRequest } from '@feedback/shared';
import { RingBuffer } from './ring-buffer.js';

const MAX_REQUESTS = 25;
const buffer = new RingBuffer<FailedRequest>(MAX_REQUESTS);
let installed = false;
let pageLoadedAt = Date.now();

/**
 * Requests to our own service are excluded — otherwise a failed submission
 * would record itself, and the retry would then carry the record of the previous
 * failure, growing the payload each time.
 */
let selfPrefix = '';

/**
 * Only failures are recorded, and only their shape: method, URL, status,
 * duration. Request and response bodies are never read — they routinely contain
 * credentials and personal data, and a screenshot tool has no business shipping
 * them to a GitHub issue.
 */
function record(entry: FailedRequest): void {
  if (selfPrefix && entry.url.startsWith(selfPrefix)) return;
  buffer.push(entry);
}

function resolveUrl(input: unknown): string {
  try {
    if (typeof input === 'string') return new URL(input, location.href).href;
    if (input instanceof URL) return input.href;
    if (input instanceof Request) return input.url;
  } catch {
    /* fall through */
  }
  return String(input ?? '');
}

function patchFetch(): void {
  const original = window.fetch;
  if (typeof original !== 'function') return;

  window.fetch = async function patchedFetch(
    this: unknown,
    ...args: Parameters<typeof fetch>
  ): Promise<Response> {
    const started = performance.now();
    const url = resolveUrl(args[0]);
    const method =
      (args[1]?.method ?? (args[0] instanceof Request ? args[0].method : 'GET')).toUpperCase();

    try {
      const response = await original.apply(this as never, args);
      if (!response.ok) {
        record({
          initiator: 'fetch',
          method: method.slice(0, 10),
          url: url.slice(0, 2_000),
          status: response.status,
          statusText: response.statusText.slice(0, 200),
          durationMs: Math.round(performance.now() - started),
          at: Date.now() - pageLoadedAt,
        });
      }
      return response;
    } catch (error) {
      // status 0 distinguishes "never completed" (DNS, CORS, offline, abort)
      // from an HTTP error response.
      record({
        initiator: 'fetch',
        method: method.slice(0, 10),
        url: url.slice(0, 2_000),
        status: 0,
        statusText: (error instanceof Error ? error.message : 'Network error').slice(0, 200),
        durationMs: Math.round(performance.now() - started),
        at: Date.now() - pageLoadedAt,
      });
      throw error;
    }
  };
}

interface TrackedXhr extends XMLHttpRequest {
  __fbMethod?: string;
  __fbUrl?: string;
  __fbStarted?: number;
}

function patchXhr(): void {
  const XHR = window.XMLHttpRequest;
  if (typeof XHR !== 'function') return;

  const originalOpen = XHR.prototype.open;
  const originalSend = XHR.prototype.send;

  XHR.prototype.open = function patchedOpen(
    this: TrackedXhr,
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    this.__fbMethod = String(method).toUpperCase();
    this.__fbUrl = resolveUrl(url);
    return (originalOpen as (...a: unknown[]) => void).call(this, method, url, ...rest);
  } as typeof XHR.prototype.open;

  XHR.prototype.send = function patchedSend(this: TrackedXhr, ...args: unknown[]) {
    this.__fbStarted = performance.now();

    const finish = (status: number, statusText: string): void => {
      record({
        initiator: 'xhr',
        method: (this.__fbMethod ?? 'GET').slice(0, 10),
        url: (this.__fbUrl ?? '').slice(0, 2_000),
        status,
        statusText: statusText.slice(0, 200),
        durationMs: Math.round(performance.now() - (this.__fbStarted ?? performance.now())),
        at: Date.now() - pageLoadedAt,
      });
    };

    this.addEventListener('load', () => {
      if (this.status >= 400) finish(this.status, this.statusText || '');
    });
    this.addEventListener('error', () => finish(0, 'Network error'));
    this.addEventListener('timeout', () => finish(0, 'Timeout'));

    return (originalSend as (...a: unknown[]) => void).apply(this, args);
  } as typeof XHR.prototype.send;
}

export function installNetworkCollector(apiBase: string): void {
  if (installed) return;
  installed = true;
  pageLoadedAt = Date.now();
  selfPrefix = apiBase;

  try {
    patchFetch();
    patchXhr();
  } catch {
    // A host page with an exotic fetch polyfill is not worth breaking over.
  }
}

export function getFailedRequests(): FailedRequest[] {
  return buffer.toArray();
}
